import React, { useMemo, useState } from "react";
import { Store } from "lucide-react";
import { toast } from "sonner";
import { api, unwrapEnvelope } from "@/lib/api";
import { formatDate, formatRelative } from "@/lib/settings/constants";
import { IntegrationCard } from "./IntegrationCard";
import { WalmartConnectModal } from "./walmart/WalmartConnectModal";
import { WalmartManageModal } from "./walmart/WalmartManageModal";

const FILTERS = ["all", "marketplace", "advertising", "payments", "productivity"];

export function IntegrationsPanel({ items, onRefresh }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [connectOpen, setConnectOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [activeProvider, setActiveProvider] = useState(null);
  const [walmartIntegration, setWalmartIntegration] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items || []).filter((item) => {
      if (filter !== "all" && item.category !== filter) return false;
      if (!q) return true;
      return (
        String(item.title || "").toLowerCase().includes(q) ||
        String(item.description || "").toLowerCase().includes(q)
      );
    });
  }, [items, query, filter]);

  function openConnect(item) {
    setActiveProvider(item.provider);
    setConnectOpen(true);
  }

  async function openManage(item) {
    if (item.provider !== "walmart_seller_central") return;
    try {
      const res = await api.walmartIntegration();
      const data = unwrapEnvelope(res);
      setWalmartIntegration(data?.integration);
      setActiveProvider(item.provider);
      setManageOpen(true);
    } catch {
      toast.error("Could not load Walmart integration");
    }
  }

  function handleCardAction(item) {
    if (item.status === "coming_soon") return;
    if (item.provider === "walmart_seller_central") {
      if (
        item.status === "connected" ||
        item.status === "needs_attention" ||
        item.status === "error"
      ) {
        openManage(item);
        return;
      }
    }
    openConnect(item);
  }

  function handleIntegrationUpdated(next) {
    onRefresh?.();
    setWalmartIntegration(next ?? null);
  }

  return (
    <div className="space-y-4" data-testid="settings-integrations">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
        <input
          type="search"
          placeholder="Search integrations"
          className="h-9 px-3 rounded-ctrl border border-edge-subtle text-compact w-full sm:max-w-xs"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="integrations-search"
        />
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              data-testid={`integrations-filter-${f}`}
              onClick={() => setFilter(f)}
              className={
                filter === f
                  ? "h-8 px-2 rounded-ctrl bg-emerald-50 text-emerald-700 text-micro font-mono uppercase"
                  : "h-8 px-2 rounded-ctrl text-micro font-mono uppercase text-ink-muted hover:bg-surface-2"
              }
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="integration-grid">
        {filtered.map((item) => (
          <IntegrationCard
            key={item.provider}
            provider={item.provider}
            icon={<Store className="h-4 w-4 text-ink-muted" />}
            title={item.title}
            description={item.description}
            status={item.status}
            connectedAt={formatDate(item.connected_at)}
            lastSyncAt={formatRelative(item.last_sync_at || item.last_verified_at)}
            onAction={() => handleCardAction(item)}
          />
        ))}
      </div>

      {activeProvider === "walmart_seller_central" && (
        <>
          <WalmartConnectModal
            open={connectOpen}
            onOpenChange={setConnectOpen}
            onConnected={() => onRefresh?.()}
          />
          <WalmartManageModal
            integration={walmartIntegration}
            open={manageOpen}
            onClose={() => setManageOpen(false)}
            onUpdated={handleIntegrationUpdated}
          />
        </>
      )}
    </div>
  );
}
